import React, { useEffect, useState } from "react";
import { getBelts, saveBelts } from "../services/beltService";
import { Belt } from "../types/belt";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Spinner } from "../components/ui/Spinner";

const BeltManagement: React.FC = () => {
  const [belts, setBelts] = useState<Belt[]>([]);
  const [editId, setEditId] = useState<number | null>(null);
  const [editPoints, setEditPoints] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBelts();
  }, []);

  const fetchBelts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getBelts();
      setBelts(data);
    } catch (err) {
      setError('Failed to fetch belts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id: number, points: number) => {
    setEditId(id);
    setEditPoints(points);
  };

  const handleSave = async (id: number) => {
    const updatedBelts = belts.map(b =>
      b.id === id ? { ...b, pointThreshold: editPoints } : b
    );
    await saveBelts(updatedBelts);
    setBelts(updatedBelts);
    setEditId(null);
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex items-center justify-center h-32">
            <Spinner label="Loading belts..." />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex items-center justify-center h-32">
            <p className="text-red-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Belt Management</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Belt Type</TableHead>
                <TableHead>Belt Color</TableHead>
                <TableHead>Points</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {belts.map(belt => (
                <TableRow key={belt.id}>
                  <TableCell className="font-medium capitalize">{belt.beltType}</TableCell>
                  <TableCell>
                    <span
                      style={{
                        background: belt.themeColor,
                        color: belt.themeColor.toLowerCase() === "#fff" || belt.themeColor.toLowerCase() === "white" ? "#222" : "#fff",
                        border: belt.themeColor.toLowerCase() === "#fff" || belt.themeColor.toLowerCase() === "white" ? "1px solid #ccc" : "none",
                        display: "inline-block",
                        minWidth: 80,
                        textAlign: "center",
                        borderRadius: 9999,
                        fontWeight: 600,
                        fontSize: 14,
                        boxShadow: "0 2px 8px 0 rgba(65, 56, 56, 0.08)",
                        padding: "0.35em 1.5em",
                        transition: "transform 0.15s cubic-bezier(.4,0,.2,1)",
                        cursor: "pointer"
                      }}
                      title={belt.themeColor}
                      onMouseOver={e => (e.currentTarget.style.transform = 'scale(1.05)')}
                      onMouseOut={e => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                      {belt.beltType.charAt(0).toUpperCase() + belt.beltType.slice(1)}
                    </span>
                  </TableCell>
                  <TableCell>
                    {editId === belt.id ? (
                      <Input
                        type="number"
                        value={editPoints}
                        onChange={e => setEditPoints(Number(e.target.value))}
                        className="w-20"
                      />
                    ) : (
                      belt.pointThreshold
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {editId === belt.id ? (
                      <Button
                        variant="default"
                        onClick={() => handleSave(belt.id)}
                        size="sm"
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        variant="default"
                        onClick={() => handleEdit(belt.id, belt.pointThreshold)}
                        size="sm"
                      >
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default BeltManagement; 